import { useEffect, useState } from 'react';
import { useAuth } from '../../../contexts/AuthProvider';
import { PageContainer } from '../../../components/PageContainer';
import { deletePurchaseOrder, fetchPurchaseOrderList, PurchaseOrderExtendedModel, PurchaseOrderModel } from '../../../model/purchase-order.model';
import { NotFound } from '../../../components/NotFound';
import { Card, Dropdown } from 'react-bootstrap';
import { formatDate, numberDisplay } from '../../../services/formats';
import { Link, useNavigate } from 'react-router';
import { baseURL } from '../../../constants/config';
import { useUtility } from '../../../contexts/UtilityContext';
import { appURL } from '../../../services/rest-api';
import { Modal } from '../../../components/Modal';
import { Iframe } from '../../../components/Iframe';
import { Actions } from '../../../components/Actions';
import PagingComponent from '../paging/paging';
import { Paging } from '../../../model/paging.model';

const android = (window as any).Android;

export default function PurchaseOrderList() {
  const navigate = useNavigate();
  const [list, setList] = useState<Array<PurchaseOrderExtendedModel>>([]);
  const [paging, setPaging] = useState<Paging>();
  const { authInfo, globalSearch } = useAuth();
  const { isLoading, showLoader, hideLoader, confirm } = useUtility();
  const [printItem, setPrintItem] = useState<{ title: string, src: string }>();

  const fetchData = async (page: number, inputs: any) => {
    try {
      showLoader();
      const res = await fetchPurchaseOrderList(page, inputs, authInfo);
      setList(res.data);
      setPaging(res.paging);
    } catch (err) {
      console.error(err);
    } finally {
      hideLoader();
    }
  };

  async function handleDelete(item: PurchaseOrderModel) {
    try {
      const isConfirmed = await confirm(`Are you want to delete the Purchase Order # ${item.po_no}`);
      if (isConfirmed && item.id) {
        await deletePurchaseOrder(item.id, authInfo);
        setList(prev => prev.filter(p => p.id !== item.id));
      }
    } catch (err) {
      console.error(err);
    }
  }

  function handlePdf(item: PurchaseOrderModel, action: string) {
    const url = `${appURL}pdf-po/${action}/${item.id}?auth=${authInfo.user.authorization}`;
    if (android) {
      android.downloadAndSharePdf(`Purchase Order # ${item.po_no}`, url);
    } else {
      setPrintItem({
        title: `Print PO # ${item.po_no}`,
        src: url,
      });
    }
  }

  function handlePrint(item: PurchaseOrderModel, action: string) {
    setPrintItem({
      title: `Print PO # ${item.po_no}`,
      src: `${appURL}prt-po/${action}/${item.id}?auth=${authInfo.user.authorization}`,
    });
  }

  useEffect(() => {
    fetchData(1, globalSearch.fields);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authInfo, globalSearch.fields]);

  return (
    <>
      <PageContainer
        title="Purchase Order"
        addButtonHandler={() => navigate(`${baseURL}po-form/add`)}
        body={!isLoading && list.length === 0 ? <NotFound /> : <>
          {list.map((item, index) => (
            <Card key={`card-${index}`} className={`mb-3${item.recd_per >= 99 ? " text-bg-success bg-opacity-10" : ""}`}>
              <Card.Body>
                <Card.Title className="d-flex justify-content-between align-items-center">
                  <span>#{item.po_no}</span>
                  <Actions index={index}>
                    <Link to={`${baseURL}po-form/edit/${item.id}`} className="dropdown-item">Edit</Link>
                    <Dropdown.Item onClick={() => handleDelete(item)}>Delete</Dropdown.Item>
                    <Dropdown.Divider />
                    <Dropdown.Item onClick={() => handlePdf(item, 'print')}>Share PDF</Dropdown.Item>
                    <Dropdown.Item onClick={() => handlePdf(item, 'printx')}>Share PDF (Qnty)</Dropdown.Item>
                    <Dropdown.Divider />
                    <Dropdown.Item onClick={() => handlePrint(item, 'print')}>Print Preview</Dropdown.Item>
                    <Dropdown.Item onClick={() => handlePrint(item, 'printx')}>Print Preview (Qnty)</Dropdown.Item>
                  </Actions>
                </Card.Title>
                <Card.Subtitle className="mb-2 text-muted">{item.ac_name}</Card.Subtitle>
                <Card.Text className='d-flex justify-content-between align-items-center'>
                  <span className='text-center'>Dated<br />{formatDate(item.po_date)}</span>
                  <span className='text-center'>Qnty<br />{numberDisplay(item.qty)}</span>
                  <span className='text-center'>Amount<br />{numberDisplay(item.amt)}</span>
                  <span className='text-center'>Recd Qnty<br />{numberDisplay(item.recd_qty)}</span>
                </Card.Text>
              </Card.Body>
            </Card>
          ))}
          <PagingComponent paging={paging} onClick={(page) => fetchData(page, globalSearch.fields)} />
        </>
        }
      />
      <Modal
        title={printItem?.title}
        show={!!printItem}
        handleClose={() => setPrintItem(undefined)}
        fullscreen
      >
        <Iframe src={printItem?.src} title={printItem?.title} />
      </Modal>
    </>
  );
};